import React, { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { RefreshCw, Unlink, ExternalLink } from 'lucide-react';
import { crudOperations } from '../../../../services/api';
import { ERROR_MESSAGES } from '../../utils/settingsConstants';
import ConfirmDialog from '../../../ConfirmDialog';
import useConfirm from '../../../../hooks/useConfirm';

const STATUS_CONFIG = {
  connected: { label: 'Connected', color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
  expired: { label: 'Expired', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50' },
  error: { label: 'Error', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' },
  not_connected: { label: 'Not Connected', color: 'bg-gray-400', textColor: 'text-gray-600', bgColor: 'bg-gray-50' }
};

/**
 * QuickBooks integration settings tab
 * Handles OAuth credentials configuration, franchisee connections, and integration status
 */
const QuickBooksTab = ({ isAdmin, userProfile, qbSettings, onSettingsChange, onSave, loading, showNotification }) => {
  const { confirmDialog, confirm } = useConfirm();
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // franchiseeId of active action

  const fetchConnections = useCallback(async () => {
    try {
      setConnectionsLoading(true);
      const response = await crudOperations.get('/admin/qb-connections');
      setConnections(response.data?.connections || []);
    } catch (error) {
      console.error('Error fetching QB connections:', error);
    } finally {
      setConnectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchConnections();
    }
  }, [isAdmin, fetchConnections]);

  // Listen for OAuth completion via BroadcastChannel
  useEffect(() => {
    if (!isAdmin) return;

    let channel;
    try {
      channel = new BroadcastChannel('qb-oauth');
      channel.onmessage = (event) => {
        if (event.data?.type === 'QB_OAUTH_COMPLETE') {
          if (event.data.success) {
            showNotification?.('QuickBooks connected successfully!');
          } else {
            showNotification?.('QuickBooks connection failed', 'error');
          }
          fetchConnections();
          setActionLoading(null);
        }
      };
    } catch (e) {
      // BroadcastChannel not supported
    }
    return () => {
      try { channel?.close(); } catch (e) {}
    };
  }, [isAdmin, fetchConnections, showNotification]);

  const handleReconnect = (franchiseeId) => {
    const sid = uuidv4();
    setActionLoading(franchiseeId);

    const popup = window.open(
      `/api/quickbooks/start?franchiseeId=${franchiseeId}&sid=${sid}`,
      'qb_oauth_popup',
      'width=600,height=700,scrollbars=yes'
    );

    // Poll for popup close as fallback
    if (popup) {
      const interval = setInterval(() => {
        if (popup.closed) {
          clearInterval(interval);
          // Delay to let BroadcastChannel fire first
          setTimeout(() => {
            fetchConnections();
            setActionLoading(null);
          }, 2000);
        }
      }, 1000);
    } else {
      setActionLoading(null);
      showNotification?.('Popup blocked — please allow popups for this site', 'error');
    }
  };

  const handleDisconnect = async (franchiseeId, name) => {
    const ok = await confirm({
      title: 'Disconnect QuickBooks',
      message: `Disconnect QuickBooks for ${name}? They will need to reconnect to use QB features.`,
      variant: 'warning',
      confirmLabel: 'Disconnect'
    });
    if (!ok) return;
    try {
      setActionLoading(franchiseeId);
      await crudOperations.delete(`/quickbooks/connection/${franchiseeId}`);
      showNotification?.(`Disconnected QuickBooks for ${name}`);
      fetchConnections();
    } catch (error) {
      console.error('Error disconnecting QB:', error);
      showNotification?.('Failed to disconnect QuickBooks', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500">{ERROR_MESSAGES.ACCESS_DENIED}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Credentials Config */}
      <div>
        <div className="flex items-center space-x-3 mb-4">
          <img src="/quickbooks-logo.svg" alt="QuickBooks" className="w-8 h-8" />
          <h3 className="text-lg font-semibold text-gray-900">QuickBooks Integration</h3>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Configure your QuickBooks application credentials. These will be used for all franchisee connections.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              QuickBooks Client ID *
            </label>
            <input
              type="text"
              value={qbSettings.clientId}
              onChange={(e) => onSettingsChange({ clientId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-transparent"
              placeholder="Enter your QuickBooks Client ID"
            />
            <p className="text-xs text-gray-500 mt-1">
              Found in your QuickBooks Developer Dashboard
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              QuickBooks Client Secret *
            </label>
            <input
              type="password"
              value={qbSettings.clientSecret}
              onChange={(e) => onSettingsChange({ clientSecret: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-transparent"
              placeholder="Enter your QuickBooks Client Secret"
            />
            <p className="text-xs text-gray-500 mt-1">
              Keep this secret secure - it's used for OAuth authentication
            </p>
          </div>

          <div className="pt-4">
            <button
              onClick={onSave}
              disabled={loading}
              className="bg-pest-green hover:bg-green-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors duration-200"
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Franchisee Connections */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Franchisee Connections</h3>
          <button
            onClick={fetchConnections}
            disabled={connectionsLoading}
            className="text-sm text-pest-blue hover:text-blue-700 flex items-center gap-1"
          >
            <RefreshCw size={14} className={connectionsLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {connectionsLoading && connections.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <RefreshCw className="mx-auto animate-spin mb-2" size={24} />
            Loading connections...
          </div>
        ) : connections.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No franchisees found.</p>
        ) : (
          <div className="space-y-3">
            {connections.map((conn) => {
              const cfg = STATUS_CONFIG[conn.status] || STATUS_CONFIG.not_connected;
              const isActionActive = actionLoading === conn.franchiseeId;

              return (
                <div key={conn.franchiseeId} className={`p-4 border border-gray-200 rounded-lg ${cfg.bgColor}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2.5 h-2.5 rounded-full ${cfg.color} flex-shrink-0`}></span>
                        <span className="font-medium text-gray-900 truncate">{conn.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.bgColor} ${cfg.textColor} border`}>
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 ml-5">{conn.email}</p>
                      {conn.companyId && (
                        <p className="text-xs text-gray-500 ml-5 mt-0.5">
                          Company ID: {conn.companyId}
                        </p>
                      )}
                      {conn.lastError && (
                        <p className="text-xs text-red-600 ml-5 mt-0.5 truncate" title={conn.lastError}>
                          Error: {conn.lastError}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <button
                        onClick={() => handleReconnect(conn.franchiseeId)}
                        disabled={isActionActive}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-pest-blue hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                        title={conn.status === 'connected' ? 'Reconnect' : 'Connect'}
                      >
                        {isActionActive ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <ExternalLink size={14} />
                        )}
                        {conn.status === 'not_connected' ? 'Connect' : 'Reconnect'}
                      </button>

                      {conn.status !== 'not_connected' && (
                        <button
                          onClick={() => handleDisconnect(conn.franchiseeId, conn.name)}
                          disabled={isActionActive}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-white hover:bg-red-50 disabled:bg-gray-100 text-red-600 border border-red-300 rounded-lg transition-colors"
                          title="Disconnect"
                        >
                          <Unlink size={14} />
                          Disconnect
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Integration Status */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Integration Status</h3>
        <div className="space-y-3">
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${qbSettings.clientId && qbSettings.clientSecret ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
              <span className="text-sm font-medium">
                Configuration: {qbSettings.clientId && qbSettings.clientSecret ? 'Complete' : 'Incomplete'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {qbSettings.clientId && qbSettings.clientSecret
                ? 'QuickBooks integration is configured and ready for use'
                : 'Please enter both Client ID and Secret to enable QuickBooks integration'
              }
            </p>
          </div>
        </div>
      </div>

      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default QuickBooksTab;
